'use client'

import { createContext, useContext, useState, ReactNode } from 'react'

export const NAV_THEMES = [
  'linear-gradient(90deg, var(--neu-brand-from) 0%, var(--neu-brand-to) 100%)',
  'linear-gradient(90deg, #4b5563 0%, #6b7280 100%)',
  'linear-gradient(90deg, #171717 0%, #262626 100%)',
  'linear-gradient(90deg, #b8860b 0%, #ffd700 100%)'
] as const

interface NavThemeValue {
  navThemeIdx: number
  cycleNavTheme: () => void
  shineEnabled: boolean
  toggleShine: () => void
  themeCount: number
  useNewUI: boolean
  toggleNewUI: () => void
  /** Transfer Market page layout: 1 = current, 2 = legacy (~June 2026) snapshot. */
  transferLayout: 1 | 2
  setTransferLayout: (layout: 1 | 2) => void
}

const NavThemeContext = createContext<NavThemeValue | null>(null)

export function NavThemeProvider({ children }: { children: ReactNode }) {
  const [navThemeIdx, setNavThemeIdx] = useState(0)
  const [shineEnabled, setShineEnabled] = useState(true)
  const [useNewUI, setUseNewUI] = useState(true)
  const [transferLayout, setTransferLayout] = useState<1 | 2>(1)

  const value: NavThemeValue = {
    navThemeIdx,
    cycleNavTheme: () => setNavThemeIdx((i) => (i + 1) % NAV_THEMES.length),
    shineEnabled,
    toggleShine: () => setShineEnabled((v) => !v),
    themeCount: NAV_THEMES.length,
    useNewUI,
    toggleNewUI: () =>
      setUseNewUI((v) => {
        if (v) {
          setShineEnabled(false)
          setNavThemeIdx(0)
        }
        return !v
      }),
    transferLayout,
    setTransferLayout
  }

  return <NavThemeContext.Provider value={value}>{children}</NavThemeContext.Provider>
}

export function useNavTheme(): NavThemeValue {
  const ctx = useContext(NavThemeContext)
  if (!ctx) {
    return {
      navThemeIdx: 0,
      cycleNavTheme: () => {},
      shineEnabled: false,
      toggleShine: () => {},
      themeCount: NAV_THEMES.length,
      useNewUI: true,
      toggleNewUI: () => {},
      transferLayout: 1,
      setTransferLayout: () => {}
    }
  }
  return ctx
}
